// Process-lifetime LRU cache for entity resolution.
// Each entry is { data, status } where status is 'loading'|'ok'|'miss'.
// Cap at 500 entries; evict oldest on overflow.

import { useState, useEffect } from 'react';

const MAX = 500;
const cache = new Map(); // urn -> { data, status }
const order = [];       // insertion order for LRU eviction

function evict() {
  while (order.length > MAX) {
    const oldest = order.shift();
    cache.delete(oldest);
  }
}

const listeners = new Map(); // urn -> Set<fn>

function notify(urn) {
  (listeners.get(urn) || new Set()).forEach((fn) => fn());
}

function subscribe(urn, fn) {
  if (!listeners.has(urn)) listeners.set(urn, new Set());
  listeners.get(urn).add(fn);
  return () => listeners.get(urn)?.delete(fn);
}

function resolve(urn) {
  if (cache.has(urn)) return;
  cache.set(urn, { data: null, status: 'loading' });
  order.push(urn);
  evict();

  fetch(`/api/entities/${encodeURIComponent(urn)}`, { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
    .then((data) => {
      cache.set(urn, { data, status: 'ok' });
      notify(urn);
    })
    .catch(() => {
      cache.set(urn, { data: null, status: 'miss' });
      notify(urn);
    });
}

export function useEntity(urn) {
  const [, tick] = useState(0);

  useEffect(() => {
    if (!urn) return;
    resolve(urn);
    return subscribe(urn, () => tick((n) => n + 1));
  }, [urn]);

  if (!urn) return { data: null, status: 'miss' };
  if (!cache.has(urn)) return { data: null, status: 'loading' };
  return cache.get(urn);
}
